/**
 * RecordHarvestModal
 *
 * Modal form for recording a harvest against an induction batch.
 */

import { useState } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import { QUALITY_GRADES } from '@/hooks/useSubstrateLogs'

export default function RecordHarvestModal({ open, onOpenChange, onSubmit, induction }) {
  const [formData, setFormData] = useState({
    harvest_date: toLocalDateString(new Date()),
    weight_kg: '',
    flush_number: String((induction?.flush_count || 0) + 1),
    quality_grade: 'A',
    harvested_by: '',
    notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      await onSubmit({
        induction_id: induction.id,
        harvest_date: formData.harvest_date,
        weight_kg: parseFloat(formData.weight_kg),
        flush_number: parseInt(formData.flush_number, 10),
        quality_grade: formData.quality_grade,
        harvested_by: formData.harvested_by || null,
        notes: formData.notes || null
      })
      onOpenChange(false)
      setFormData({
        harvest_date: toLocalDateString(new Date()),
        weight_kg: '',
        flush_number: String((induction?.flush_count || 0) + 1),
        quality_grade: 'A',
        harvested_by: '',
        notes: ''
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (!induction) return null

  const newTotal = induction.total_harvest_kg + (parseFloat(formData.weight_kg) || 0)
  const yieldPerLog = induction.log_count > 0 ? (newTotal / induction.log_count).toFixed(3) : '0'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Harvest — {induction.induction_code}</DialogTitle>
        </DialogHeader>

        {/* Induction Info */}
        <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Logs</span>
            <span className="font-medium">{induction.log_count}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Harvested so far</span>
            <span className="font-medium">{induction.total_harvest_kg} kg</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Flush</span>
            <span className="font-medium">{induction.flush_count || 0}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="harvest_date">Harvest Date</Label>
              <Input
                id="harvest_date"
                type="date"
                value={formData.harvest_date}
                onChange={(e) => handleChange('harvest_date', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="weight_kg">Weight (kg)</Label>
              <Input
                id="weight_kg"
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                value={formData.weight_kg}
                onChange={(e) => handleChange('weight_kg', e.target.value)}
                placeholder="12.5"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="flush_number">Flush Number</Label>
              <Select
                value={formData.flush_number}
                onValueChange={(val) => handleChange('flush_number', val)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(n => (
                    <SelectItem key={n} value={String(n)}>Flush {n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quality_grade">Quality Grade</Label>
              <Select
                value={formData.quality_grade}
                onValueChange={(val) => handleChange('quality_grade', val)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUALITY_GRADES.map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="harvested_by">Harvested By</Label>
            <Input
              id="harvested_by"
              value={formData.harvested_by}
              onChange={(e) => handleChange('harvested_by', e.target.value)}
              placeholder="Name..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Input
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

          {/* Running total preview */}
          {formData.weight_kg && (
            <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">New total</span>
                <span className="font-medium">{newTotal.toFixed(2)} kg</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Yield per log</span>
                <span className="font-medium">{yieldPerLog} kg</span>
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record Harvest
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
